import React, { useState, useEffect } from "react";
import {
  Stack,
  CommandBar,
  ICommandBarItemProps,
  IButtonStyles,
  CommandBarButton,
  Panel,
  PanelType,
  Nav,
  INavLinkGroup,
} from "@fluentui/react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const Header: React.FC = () => {
  const { t } = useTranslation();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsNarrow(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const itemStyles: IButtonStyles = {
    root: { backgroundColor: "transparent", color: "#333", padding: "0 10px" },
    rootHovered: { backgroundColor: "transparent", color: "#0078d4" },
    label: { fontSize: 14 },
  };

  const items: ICommandBarItemProps[] = [
    {
      key: t("navigation.links.id"),
      text: t("navigation.links.value"),
      ariaLabel: t("navigation.links.value"),
      itemProps: { styles: itemStyles },
      subMenuProps: {
        items: [
          {
            key: t("navigation.links.option1.id"),
            text: t("navigation.links.option1.value"),
            href: t("navigation.links.option1.href"),
            target: "_blank",
          },
          {
            key: t("navigation.links.option2.id"),
            text: t("navigation.links.option2.value"),
            href: t("navigation.links.option2.href"),
            target: "_blank",
          },
          {
            key: t("navigation.links.option3.id"),
            text: t("navigation.links.option3.value"),
            href: t("navigation.links.option3.href"),
            target: "_blank",
          },
        ],
      },
    },
    {
      key: "navigation.info.id",
      text: t("navigation.info.value"),
      ariaLabel: t("navigation.info.value"),
      itemProps: { styles: itemStyles },
      subMenuProps: {
        items: [
          {
            key: t("navigation.info.option1.id"),
            text: t("navigation.info.option1.value"),
            href: t("navigation.info.option1.href"),
            target: "_self",
          },
          {
            key: t("navigation.info.option2.id"),
            text: t("navigation.info.option2.value"),
            href: t("navigation.info.option2.href"),
            target: "_self",
          },
        ],
      },
    },
    {
      key: "navigation.products.id",
      text: t("navigation.products.value"),
      ariaLabel: t("navigation.products.value"),
      itemProps: { styles: itemStyles },
      subMenuProps: {
        items: [
          {
            key: t("navigation.products.option1.id"),
            text: t("navigation.products.option1.value"),
            href: t("navigation.products.option1.href"),
            target: "_self",
          },
        ],
      },
    },
    {
      key: "navigation.pricing.id",
      text: t("navigation.pricing.value"),
      ariaLabel: t("navigation.pricing.value"),
      itemProps: { styles: itemStyles },
      subMenuProps: {
        items: [
          {
            key: t("navigation.pricing.option1.id"),
            text: t("navigation.pricing.option1.value"),
            href: t("navigation.pricing.option1.href"),
            target: "_self",
          },
        ],
      },
    },
  ];

  // Multi-level navigation links for the side panel
  const navLinkGroups: INavLinkGroup[] = [
    {
      name: t("navigation.links.value"),
      links: [
        {
          key: t("navigation.links.option1.id"),
          name: t("navigation.links.option1.value"),
          url: t("navigation.links.option1.href"),
          target: "_blank",
        },
        {
          key: t("navigation.links.option2.id"),
          name: t("navigation.links.option2.value"),
          url: t("navigation.links.option2.href"),
          target: "_blank",
        },
        {
          key: t("navigation.links.option3.id"),
          name: t("navigation.links.option3.value"),
          url: t("navigation.links.option3.href"),
          target: "_blank",
        },
      ],
    },
    {
      name: t("navigation.info.value"),
      links: [
        {
          key: t("navigation.info.option1.id"),
          name: t("navigation.info.option1.value"),
          url: t("navigation.info.option1.href"),
          target: "_self",
        },
        {
          key: t("navigation.info.option2.id"),
          name: t("navigation.info.option2.value"),
          url: t("navigation.info.option2.href"),
          target: "_self",
        },
      ],
    },
    {
      name: t("navigation.products.value"),
      links: [
        {
          key: t("navigation.products.option1.id"),
          name: t("navigation.products.option1.value"),
          url: t("navigation.products.option1.href"),
          target: "_self",
        },
      ],
    },
    {
      name: t("navigation.pricing.value"),
      links: [
        {
          key: t("navigation.pricing.option1.id"),
          name: t("navigation.pricing.option1.value"),
          url: t("navigation.pricing.option1.href"),
          target: "_self",
        },
      ],
    },
  ];

  return (
    <Stack
      horizontal
      tokens={{ childrenGap: 5 }}
      styles={{ root: { padding: "0px" } }}
    >
      {/* Burger Menu Button */}
      <CommandBarButton
        iconProps={{
          iconName: "GlobalNavButton",
          styles: { root: { fontSize: 24, color: "grey" } },
        }}
        styles={{ root: { marginRight: 10 } }}
        onClick={() => setIsPanelOpen(true)}
        ariaLabel="Open Navigation"
      />

      {/* Side Panel for Multi-Level Navigation */}
      <Panel
        isOpen={isPanelOpen}
        onDismiss={() => setIsPanelOpen(false)}
        type={PanelType.smallFixedNear}
        headerText="Menu"
        closeButtonAriaLabel="Close"
      >
        <Nav
          groups={navLinkGroups}
          onLinkClick={(event, link) => {
            if (link && link.url) {
              window.location.href = link.url;
            }
          }}
          styles={{
            root: {
              width: "100%",
              height: "100%",
              boxSizing: "border-box",
              overflowY: "auto",
            },
          }}
        />
      </Panel>

      <Link to="/">
        <Stack horizontal tokens={{ childrenGap: 0 }} verticalAlign="center">
          <img
            src={process.env.PUBLIC_URL + "/images/Delta_Flame_No_Words.png"}
            alt="Delta Flame Logo"
            style={{ width: "auto", height: "50px" }}
          />
          <img
            src={process.env.PUBLIC_URL + "/images/DeltaFlame_Text_Only.png"}
            alt="Delta Flame"
            style={{ width: "200px", height: "23px" }}
          />
        </Stack>
      </Link>
      <Stack
        horizontal
        tokens={{ childrenGap: 0 }}
        horizontalAlign="end"
        styles={{
          root: { marginTop: "2px", display: isNarrow ? "none" : "flex" },
        }}
      >
        <CommandBar
          items={items}
          styles={{
            root: {},
          }}
        />
      </Stack>
    </Stack>
  );
};

export default Header;
